<template>
  <header class="leftbar">
    <div class="leftbar__inner">
      <div
        class="d-flex flex-row sm:hidden xl:flex sm:flex-col w-full justify-around items-center mb-2"
        :class="{ 'pt-[18px]': !user }"
        v-if="!isUserAuth">
        <div class="leftbar__items">
          <router-link to="/about" class="leftbar__overlay-item text-foregroundPrimary">
            <ChannelIcon class="mr-2 w-[20px]" />
            About Us
          </router-link>
        </div>
        <BaseButton class="max-w-[100px] sign-in-button" @click="logIn">Sign in</BaseButton>
        <BaseButton class="max-w-[100px] sign-up-button" @click="signUp">Sign up</BaseButton>
      </div>
      <div
        v-if="isUserAuth && userdata"
        class="leftbar__nav w-full mt-6 hidden sm:flex justify-start flex-col items-start">
        <div
          v-for="links in navLinks.filter(el => !el.hidden)"
          :key="links.name"
          @click.prevent="toggleLink(links, links.id)"
          :class="{ 'min-[1025px]:hidden': links.name === 'Explore' }">
          <component
            :is="links.name === 'Create' ? 'div' : 'router-link'"
            :to="links.name !== 'Create' ? getLink(links) : undefined"
            class="leftbar__items flex mb-[20px]"
            :class="{
              'pointer-events-none': links.disabled,
              active: links.isActive,
              full__size: isFullMenuItems,
              'create-button': links.name === 'Create',
            }"
            @click="links.name === 'Create'">
            <div class="flex justify-center items-start" :class="{ 'ml-[16px]': links.name !== 'Create' }">
              <div class="w-[20px] h-[20px]">
                <img :src="getIconPath(links).value" alt="icon" />
              </div>
              <span
                class="leftbar__label text-foregroundPrimary text-[16px] cursor-pointer"
                :class="{
                  'mt-1': links.name === 'Home',
                  'hidden xl:block': !isFullMenuItems,
                  'd-block': isFullMenuItems,
                  'font-weight': links.isActive,
                  'create-label': links.name === 'Create',
                }"
                >{{ links.name }}</span
              >
              <div
                v-if="links.id === 5"
                class="bg-[#a3e635] text-[#000] p-1 text-[12px] rounded-[5px] ml-1 -mt-[1px] max-h-[15px] flex items-center">
                <span>Beta</span>
              </div>
            </div>
          </component>
        </div>
      </div>
      <div v-if="userdata" class="leftbar__nav w-full mt-[12px] mb-[10px] flex sm:hidden justify-between items-center">
        <div
          v-for="links in navLinks.filter(el => !el.hiddenForMobile)"
          :key="links.name"
          @click.prevent="toggleLink(links, links.id)">
          <div
            v-if="links.name === 'Create'"
            class="leftbar__items flex content-center items-center create-button-mobile"
            :class="{ active: links.isActive }">
            <img class="cursor-pointer" :src="getIconPath(links).value" alt="icon" />
          </div>
          <router-link
            v-else
            :to="links.src"
            class="leftbar__items flex content-center items-center relative"
            :class="{ 'pointer-events-none': links.disabled, active: links.isActive }">
            <img class="cursor-pointer" :src="getIconPath(links).value" alt="icon" />
            <div
              v-if="links.id === 5"
              class="bg-[#a3e635] text-[#000] p-[2px] text-[10px] rounded-[4px] absolute -top-1 right-1 flex items-center">
              <span>Beta</span>
            </div>
          </router-link>
        </div>
        <div class="flex justify-center items-center cursor-pointer relative">
          <button
            class="leftbar__items leftbar__items-mobile h-[24px] text-foregroundPrimary"
            @click.stop="toggleLeftbarOverlay">
            <MenuIcon />
          </button>
          <div
            v-show="isLeftbarOverlayVisible"
            @click="closeLeftbarOverlay"
            class="leftbar__overlay flex flex-col text-sm">
            <RouterLink to="/about" class="leftbar__overlay-item">
              <ChannelIcon class="mr-2 w-[20px]" />
              About Us
            </RouterLink>
            <RouterLink to="/profile/66ec8452e2fecf304bbc051b" class="leftbar__overlay-item">
              <lightBulbIcon class="mr-2 w-[20px]" />
              Help
            </RouterLink>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch, watchEffect } from 'vue';

import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import ChannelIcon from '@/components/Icons/ChannelIcon.vue';
import LightBulbIcon from '@/components/Icons/LightBulbIcon.vue';
import MenuIcon from '@/components/Icons/MenuIcon.vue';
import AboutUsLink from '@/components/Leftbar/AboutUsLink.vue';
import { environment } from '@/global/baseUrls.js';
import { setDefaultTab } from '@/utils/activeTabUtils';
import { useSchemeObserver } from '@/utils/colorSchemeUtils.js';
import eventBus from '@/utils/eventBus';
import { scrollToTop } from '@/utils/scrollControl';

const store = useStore();
const router = useRouter();
const route = useRoute();
const currentScheme = useSchemeObserver();

const isLinkActive = link => {
  const path = route.fullPath;

  if (link.name === 'Home' && path === '/') return true;
  if (link.name === 'Explore' && path.startsWith('/search')) return true;
  if (link.name === 'Alerts' && path.startsWith('/notifications')) return true;
  if (link.name === 'Help' && path.includes('/user/sezushelp')) return true;
  if (link.name === 'About Us' && path.startsWith('/about')) return true;
  if (link.name === 'Orgs/Groups' && path.startsWith('/orgs-groups')) return true;

  return false;
};

const logOut = payload => store.dispatch('logOut', payload);

//const getOnlyUserPosts = (payload) => store.dispatch("setOnlyUserPosts", { id: payload, limit: "1" });
const setShowModal = value => store.dispatch('showModal', value);
const setEditData = data => store.dispatch('addModalDataToState', data);
const setIsReply = value => store.dispatch('setIsReply', value);
const user = localStorage.getItem('user');
const isShowModal = computed(() => store.getters.getShowModalStatus);
const isUserAuth = computed(() => store.getters.getAuthStatus);
const userdata = computed(() => store.getters.getUser);
const environments = ref(['https://dev.sez.us', 'https://stg.sez.us', 'https://sez.us']);
const isFullMenuItems = ref(false);

const unreadNotificationsCount = computed(() => store.getters.getUnreadNotificationsCount);
const userRoute = computed(() => {
  return `/user/sezushelp`;
});

const navLinks = ref([
  {
    icon: 'Home.svg',
    id: 1,
    iconActive: 'Homefill.svg',
    name: 'Home',
    src: '/',
    isActive: false,
    disabled: false,
    hidden: false,
    hiddenForMobile: false,
  },
  {
    icon: 'Search.svg',
    iconActive: 'Search.svg',
    name: 'Explore',
    id: 2,
    src: '/search',
    isActive: false,
    disabled: false,
    hidden: false,
    hiddenForMobile: false,
  },
  {
    id: 3,
    icon: 'Channel.svg',
    iconActive: 'Channel.svg',
    name: 'About Us',
    src: '/about',
    isActive: false,
    disabled: false,
    hidden: false,
    hiddenForMobile: true,
  },
  {
    id: 4,
    icon: 'Alert.svg',
    iconActive: 'Alertfill.svg',
    name: 'Alerts',
    src: '/notifications',
    isActive: false,
    disabled: false,
    hidden: false,
    hiddenForMobile: true,
  },
  {
    id: 5,
    icon: 'Groups.svg',
    iconActive: 'Groupsfill.svg',
    name: 'Groups',
    src: '/orgs-groups',
    isActive: false,
    disabled: false,
    hidden: false,
    hiddenForMobile: false,
  },
  {
    id: 6,
    icon: 'lightbulb.svg',
    iconActive: 'lightbulb.svg',
    name: 'Help',
    src: userRoute,
    isActive: false,
    disabled: false,
    hidden: false,
    hiddenForMobile: true,
  },
  {
    id: 7,
    icon: 'Create.svg',
    iconActive: 'Create.svg',
    name: 'Create',
    isActive: false,
    disabled: false,
    hidden: false,
    hiddenForMobile: false,
  },
]);

const getIconPath = link => {
  return computed(() => {
    const scheme = currentScheme.value;
    if (link.name === 'Create') {
      // Для кнопки Create всегда используем темную иконку, т.к. фон кнопки светлый
      return `/icon/Create-dark.svg`;
    }
    return link.isActive
      ? `/icon/${link.iconActive.replace('.svg', scheme === 'light' ? '-dark.svg' : '.svg')}`
      : `/icon/${link.icon.replace('.svg', scheme === 'light' ? '-dark.svg' : '.svg')}`;
  });
};

const getLink = link => {
  if (!link.src) return;
  return link.src;
};

const setNotificationIcon = newCount => {
  const alertLink = navLinks.value.find(link => link.id === 4);

  if (alertLink) {
    const hasMessages = newCount > 0;

    if (alertLink.isActive) {
      alertLink.icon = hasMessages ? 'alert-unread-fill.svg' : 'Alertfill.svg';
      alertLink.iconActive = alertLink.icon;
    } else {
      alertLink.icon = hasMessages ? 'alert-unread.svg' : 'Alertfill.svg';
      alertLink.iconActive = alertLink.icon;
    }
  }
};

watch(
  unreadNotificationsCount,
  newCount => {
    setNotificationIcon(newCount);
  },
  { immediate: true },
);

onMounted(() => {
  navLinks.value.forEach(link => {
    link.isActive = isLinkActive(link);
  });

  setNotificationIcon(unreadNotificationsCount.value);
});

watch(route, () => {
  navLinks.value.forEach(link => {
    link.isActive = isLinkActive(link);
  });
});

watchEffect(() => {
  if (!isShowModal.value) {
    const createButton = navLinks.value.find(el => el.name === 'Create');
    if (createButton) createButton.isActive = false;

    // После закрытия модального окна восстанавливаем активное состояние для текущего маршрута
    navLinks.value.forEach(link => {
      if (link.name !== 'Create') {
        link.isActive = isLinkActive(link);
      }
    });
  }
});

const toggleLink = async (item, id) => {
  isFullMenuItems.value = true;
  if (item.name === 'Home') {
    eventBus.emit('set-default-tab');
    setTimeout(() => {
      scrollToTop();
    }, 100);
  }

  navLinks.value.forEach(el => (el.isActive = false));
  item.isActive = true;

  if (item.name === 'Create') {
    store.dispatch('getMe');
    setTimeout(() => {
      setIsReply(false);
      setEditData(null);
      setShowModal(true);
    }, 100);
    return;
  }
};

const logIn = () => {
  router.push('/sign-in');
  localStorage.removeItem('activeTab');
};
const signUp = () => {
  router.push('/onboarding/login-method');
};

const togglelogOut = () => {
  logOut();
};

const isLeftbarOverlayVisible = ref(false);
const toggleLeftbarOverlay = () => {
  isLeftbarOverlayVisible.value = !isLeftbarOverlayVisible.value;
};
const closeLeftbarOverlay = () => {
  isLeftbarOverlayVisible.value = false;
};
const closeLeftbarOverlayOnOutsideClick = event => {
  if (!event.target.closest('.leftbar__overlay')) {
    isLeftbarOverlayVisible.value = false;
  }
};
onMounted(() => {
  document.addEventListener('click', closeLeftbarOverlayOnOutsideClick);
});
onUnmounted(() => {
  document.removeEventListener('click', closeLeftbarOverlayOnOutsideClick);
});
</script>

<style lang="scss" scoped>
.leftbar__items {
  width: 60px;
  height: 40px;
  position: relative;
  z-index: 1;

  opacity: 0.5;
  display: flex;
  align-items: center;
  justify-content: start;
  cursor: pointer;

  &.active {
    opacity: var(--leftbar-menu-hover-opacity);
  }

  &.full__size {
    width: 160px;
  }

  &:hover {
    opacity: var(--leftbar-menu-hover-opacity);
  }
}

.leftbar__items-mobile {
  display: flex;
  align-items: center !important;
  justify-content: center;
}

.leftbar__label {
  margin-left: 10px;
  font-weight: 400;
}

.leftbar {
  height: fit-content;
  position: sticky;
  top: 80px;

  &__inner {
    @include container;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    position: sticky;
    top: 80px;
  }

  .nav-bar {
    width: 100%;
    background: transparent;

    &__inner {
      @include container;
      margin: 0 auto;
      height: 100%;
      display: flex;
      align-items: center;
      overflow: auto;

      &::-webkit-scrollbar {
        width: 0;
        height: 0;
        background-color: transparent;
      }

      scrollbar-color: transparent transparent;
      scrollbar-width: none;
    }

    &__items {
      position: relative;
      display: grid;
      grid-template-columns: 1fr 1fr;
      justify-items: center;
      grid-gap: 5px;
    }

    &__item {
      padding: 28px 0;
      color: #1390e5;
      transition: 0.2s ease-in-out;

      a {
        text-decoration: none;
      }

      span {
        display: block;
        color: #1390e5;
        text-decoration: none;
        transition: 0.2s ease-in-out;
        word-break: break-all;

        &:hover {
          opacity: 0.7;
        }
      }
    }

    &__shape {
      $tabsCount: 2;
      $pagePadding: 5px;
      margin: 0 $pagePadding;
      position: absolute;
      height: 2px;
      left: 0;
      bottom: 0;
      width: calc((100% / #{$tabsCount}) - #{$pagePadding * 2});
      background: #1390e5;
      transition: 0.3s ease-in-out;
    }
  }

  &__action {
    display: flex;
  }

  &__button {
    &_left {
      margin-right: 20px;
    }
  }

  @include _1279 {
    &__inner {
      padding: 0;
    }
  }

  @include _1024 {
    &__inner {
      padding-left: 29px;
    }
  }

  @include _767 {
    position: relative;
    top: auto;
    height: auto;
  }

  @include _639 {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    background-color: var(--background-primary);
    z-index: 1000;
    padding: 10px 16px;

    &__inner {
      padding-left: 0;
    }
  }

  @include _575 {
    &__button {
      font-size: 14px !important;
      padding: 0;
    }

    .nav-bar {
      &__item {
        padding: 15px 0;
        font-size: 12px;
      }
    }
  }
}

.leftbar__inner-active-menu {
  padding: 17px !important;
}

.leftbar__items::before {
  content: '';
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 4px;
  background-color: rgba(0, 0, 0, 0);
  z-index: -1;
  transition:
    background-color 0.3s ease,
    transform 0.3s ease;
}

.leftbar__items:hover::before {
  background-color: var(--background-secondary);
}

.leftbar__items.active::before {
  background-color: transparent;
}

.font-weight {
  font-weight: 700;
}

// .leftbar__items img,
// .leftbar__items svg {
//   width: 24px;
//   height: 24px;
// }

@media (min-width: 1280px) {
  .sign-in-button,
  .sign-up-button {
    margin-top: 15px;
  }

  .leftbar__nav {
    align-items: start;
  }

  .leftbar__items {
    width: 160px;
    height: 40px;
  }

  .create-button.leftbar__items {
    width: auto !important;
    padding: 6px 12px !important;
  }
}

@media (min-width: 1024px) and (max-width: 1279px) {
  .sign-in-button,
  .sign-up-button {
    margin-top: 5px;
  }
}

@media (max-width: 767px) {
  .sign-in-button,
  .sign-up-button {
    margin-top: 0;
  }
}

.create-button {
  margin-left: 6px;
  background-color: #a3e635;
  border-radius: 6px;
  opacity: 1 !important;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  padding: 6px 12px;
  height: 36px;
  width: auto !important;

  &:hover {
    opacity: 0.9 !important;
    background-color: #95d130;
  }

  &::before {
    display: none;
  }

  .create-label {
    color: #000;
    font-weight: 500;
    margin-left: 6px !important;
  }

  img {
    filter: brightness(0);
  }

  &.full__size {
    width: auto !important;
  }
}

.create-button-mobile {
  background-color: #a3e635;
  border-radius: 6px;
  opacity: 1 !important;
  height: 28px !important;
  width: 40px !important;
  display: flex;
  justify-content: center;
  padding: 6px;

  &:hover {
    opacity: 0.9 !important;
    background-color: #95d130;
  }

  &::before {
    display: none;
  }

  img {
    width: 18px;
    height: 18px;
    filter: brightness(0);
  }
}
</style>
<style lang="scss">
.leftbar__overlay {
  position: absolute;
  bottom: calc(0% + 40px);
  right: calc(100% - 24px);
  background: var(--background-primary);
  border: 1.5px solid var(--color-scheme-switcher-dropdown-border);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.24);
  border-radius: 8px;
  padding: 8px 0;
  z-index: 1;

  &-item {
    height: 36px;
    width: 180px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    color: var(--foreground-primary);
    cursor: pointer;
    padding: 0 16px;
    box-sizing: border-box;
  }

  &-item:hover {
    background: var(--background-secondary);
  }
}
</style>
